import { TrendingUp, Clock, Users, Calendar } from "lucide-react";
import equipeMedicaleImage from "@/assets/equipe-medicale.jpg";
import doctorAvatar1 from "@/assets/doctor-avatar-1.jpg";
import doctorAvatar2 from "@/assets/doctor-avatar-2.jpg";
import doctorAvatar3 from "@/assets/doctor-avatar-3.jpg";
import doctorAvatar4 from "@/assets/doctor-avatar-4.jpg";
import doctorAvatar5 from "@/assets/doctor-avatar-5.jpg";

const KeyStats = () => {
  const stats = [
    {
      icon: TrendingUp,
      value: "+500",
      label: "Missions organisées",
      gradient: "from-primary to-primary/80",
      delay: "0",
    },
    {
      icon: Clock,
      value: "≤ 2 jours",
      label: "Délai moyen de placement",
      gradient: "from-accent to-accent/80",
      delay: "100",
    },
    {
      icon: Users,
      value: "92%",
      label: "Taux de récurrence médecins",
      gradient: "from-secondary to-secondary/80",
      delay: "200",
    },
    {
      icon: Calendar,
      value: "7j/7",
      label: "Disponibilité",
      gradient: "from-primary to-secondary",
      delay: "300",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Title and Image */}
          <div>
            <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-6 leading-[1.1]">
              Donnez un nouvel élan à votre carrière médicale
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              On s'occupe du reste : <span className="font-semibold text-foreground">suivi personnalisé, missions sur-mesure, administration simplifiée</span>.
            </p>
            
            {/* Team Image */}
            <div className="relative group/img">
              <div className="absolute -inset-2 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-3xl blur-xl opacity-50 group-hover/img:opacity-75 transition-opacity"></div>
              <img 
                src={equipeMedicaleImage} 
                alt="Équipe médicale Mind Medical en consultation"
                className="relative rounded-3xl shadow-2xl border-4 border-white/40"
                width="960"
                height="540"
                loading="lazy"
                decoding="async"
              />

              {/* Trust badge overlaid on the image */}
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 inline-flex items-center gap-3 px-4 py-2 md:px-5 md:py-3 bg-white/95 backdrop-blur-md rounded-full border border-white/50 shadow-xl z-10">
                <div className="flex -space-x-3" aria-hidden="true">
                  <img src={doctorAvatar1} alt="" width="32" height="32" loading="lazy" decoding="async" className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-white object-cover" />
                  <img src={doctorAvatar2} alt="" width="32" height="32" loading="lazy" decoding="async" className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-white object-cover" />
                  <img src={doctorAvatar3} alt="" width="32" height="32" loading="lazy" decoding="async" className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-white object-cover" />
                  <img src={doctorAvatar4} alt="" width="32" height="32" loading="lazy" decoding="async" className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-white object-cover" />
                  <img src={doctorAvatar5} alt="" width="32" height="32" loading="lazy" decoding="async" className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="flex flex-col">
                  <span className="text-primary text-xs md:text-sm font-bold leading-tight">+500 médecins</span>
                  <span className="text-muted-foreground text-[10px] md:text-xs leading-tight">nous font confiance</span>
                </div>
              </div>
            </div>

          </div>

          {/* Right - Stats Grid */}
          <div className="grid grid-cols-2 gap-x-6 md:gap-x-8 gap-y-3 md:gap-y-4 items-start">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`group relative animate-fade-in ${index >= 2 ? 'self-end' : ''}`}
                style={{ animationDelay: `${stat.delay}ms` }}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-1 bg-gradient-to-br ${stat.gradient} rounded-3xl opacity-0 group-hover:opacity-30 blur-2xl transition-opacity duration-500`}></div>
                
                {/* Card */}
                <div className="relative bg-white/90 backdrop-blur-lg rounded-3xl p-8 text-center border border-white/50 hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105">
                  <div className={`w-16 h-16 mx-auto mb-6 bg-gradient-to-br ${stat.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <stat.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                  </div>
                  <p className={`font-poppins font-black text-3xl md:text-4xl bg-gradient-to-br ${stat.gradient} bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    {stat.value}
                  </p>
                  <p className="text-muted-foreground text-sm md:text-base font-medium">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground bg-white/40 backdrop-blur-sm rounded-full px-6 py-3 inline-block mx-auto border border-border/30">
          * Chiffres en cours de validation - Données S1 2025
        </p>
      </div>
    </section>
  );
};

export default KeyStats;